import { el } from "./dom.js";
import { api } from "../net/api.js";
import { friends, onFriendEvent, type Friend } from "../net/friends.js";

/**
 * Friends manager: send requests by username, accept/decline incoming ones,
 * see who's online (live), remove. Room invites are sent from the lobby.
 */
export function friendsScreen(onBack: () => void): HTMLElement {
  const screen = el("div", { class: "screen" });
  const panel = el("div", { class: "panel", style: "min-width:440px" });
  const reqEl = el("div", { style: "margin-top:0.4rem" });
  const listEl = el("div", { class: "room-list", style: "margin-top:0.6rem" });
  const msgEl = el("div", { class: "error" });
  // One status line, coloured by outcome — mirrors cosmeticsScreen: a positive
  // result reads cyan, a failure reads the danger red. Before this, EVERY
  // message (including "request sent ✓") rendered red and read as an error.
  const setMsg = (text: string, ok = false): void => {
    msgEl.style.color = ok ? "var(--cyan)" : "var(--danger)";
    msgEl.textContent = text;
  };
  let list: Friend[] = [];
  let incoming: { id: string; username: string }[] = [];
  let outgoing: { id: string; username: string }[] = [];

  const renderRequests = (): void => {
    const rows: HTMLElement[] = [];
    if (incoming.length) {
      rows.push(el("div", { class: "card-title", text: "FRIEND REQUESTS", style: "color:#ff2fd0" }));
      for (const r of incoming) {
        const acc = el("button", { class: "primary", text: "Accept", style: "padding:0 0.7em" });
        const dec = el("button", { class: "ghost", text: "Decline", style: "padding:0 0.7em;margin-left:6px" });
        acc.onclick = () => void friends.accept(r.id).then((res) => {
          incoming = incoming.filter((x) => x.id !== r.id);
          if (res.friend && !list.some((x) => x.id === res.friend.id)) list.push(res.friend);
          setMsg(`${r.username} is now your friend ✓`, true);
          renderRequests(); render();
        }).catch((e) => setMsg((e as Error).message));
        dec.onclick = () => void friends.remove(r.id)
          .then(() => { incoming = incoming.filter((x) => x.id !== r.id); renderRequests(); })
          .catch((e) => setMsg((e as Error).message));
        rows.push(el("div", { class: "room-row", style: "display:flex;align-items:center" },
          el("span", { text: r.username, style: "flex:1" }), acc, dec));
      }
    }
    if (outgoing.length) {
      rows.push(el("div", { class: "hint", style: "margin-top:0.4rem", text: `sent, waiting: ${outgoing.map((o) => o.username).join(", ")}` }));
    }
    reqEl.replaceChildren(...rows);
  };

  const render = (): void => {
    if (list.length === 0) {
      listEl.replaceChildren(el("div", { class: "hint", text: "no friends yet — send a request by username" }));
      return;
    }
    const sorted = [...list].sort((a, b) => (Number(b.online) - Number(a.online)) || a.username.localeCompare(b.username));
    listEl.replaceChildren(...sorted.map((f) => {
      const dot = el("span", { text: "●", style: `color:${f.online ? "#9dff00" : "#4a5568"};margin-right:8px` });
      const name = el("span", { text: f.username, style: "flex:1" });
      const status = el("span", { text: f.online ? "online" : "offline", style: `color:${f.online ? "#9dff00" : "#5f6b7a"};font-size:0.85em;margin-right:10px` });
      const rm = el("button", { class: "ghost", text: "✕", style: "padding:0 0.6em" });
      rm.onclick = () => void friends.remove(f.id)
        .then(() => { list = list.filter((x) => x.id !== f.id); render(); })
        .catch((e) => setMsg((e as Error).message));
      return el("div", { class: "room-row", style: "display:flex;align-items:center" }, dot, name, status, rm);
    }));
  };

  const load = (): void => {
    if (api.guest) { listEl.replaceChildren(el("div", { class: "hint", text: "create an account to add friends" })); return; }
    void friends.list().then((r) => { list = r.friends; incoming = r.incoming; outgoing = r.outgoing; renderRequests(); render(); }).catch((e) => setMsg((e as Error).message));
  };

  // Self-healing teardown: the Back button calls off() directly, but this screen
  // can also be torn down by ANY other navigation — most concretely, accepting a
  // game invite popup (main.ts:777 → enterRoom → showScreen) replaces the screen
  // without routing through Back. showScreen has no teardown hook, so without
  // this the handler leaks, re-rendering detached DOM on every friend event for
  // the rest of the session. Detecting the detached root and unsubscribing on
  // the next event self-cleans regardless of the exit path.
  const off = onFriendEvent((e) => {
    if (!screen.isConnected) { off(); return; }
    if (e.type === "friend_online") { const f = list.find((x) => x.id === e.id); if (f) { f.online = true; render(); } }
    else if (e.type === "friend_offline") { const f = list.find((x) => x.id === e.id); if (f) { f.online = false; render(); } }
    else if (e.type === "online_snapshot") { for (const f of list) f.online = e.ids.includes(f.id); render(); }
    else if (e.type === "friend_request") { if (!incoming.some((x) => x.id === e.id)) { incoming.push({ id: e.id, username: e.username }); renderRequests(); } }
    else if (e.type === "friend_accepted") load(); // they accepted → refresh accepted list
  });

  const input = el("input", { type: "text", placeholder: "friend's username", style: "flex:1" }) as HTMLInputElement;
  const addBtn = el("button", { class: "magenta", text: "Send request" });
  const doAdd = (): void => {
    const name = input.value.trim();
    if (!name) return;
    setMsg("");
    void friends.add(name).then((r) => {
      input.value = "";
      if (r.status === "accepted" && r.friend) {
        // They had already requested us, so adding them completes the pair
        // immediately — say so instead of silently growing the list.
        if (!list.some((x) => x.id === r.friend!.id)) list.push(r.friend);
        setMsg(`${r.friend.username} is now your friend ✓`, true);
        render();
      } else { setMsg("request sent ✓", true); load(); }
    }).catch((e) => setMsg((e as Error).message));
  };
  addBtn.onclick = doAdd;
  input.onkeydown = (e) => { if (e.key === "Enter") doAdd(); };

  const back = el("button", { class: "ghost wide mt1", text: "Back" });
  back.onclick = () => { off(); onBack(); };

  panel.append(
    el("div", { class: "brand", text: "NEON PURGE" }),
    el("div", { class: "brand-sub", text: "FRIENDS" }),
    el("div", { class: "row", style: "gap:0.5rem;margin-top:0.4rem" }, input, addBtn),
    msgEl,
    reqEl,
    listEl,
    back,
  );
  screen.append(panel);
  load();
  return screen;
}
